import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { eur } from "../helpers";
import { Lock, Printer, FileDown, Mail, MessageCircle, Link2, Send, Clock, ShieldCheck } from "lucide-react";

const onlyDigits = (s) => (s || "").replace(/[^\d]/g, "");
const fmtDT = (s) => { try { return new Date(s).toLocaleString("it-IT"); } catch { return s; } };

export default function AuditDetailDialog({ open, onOpenChange, logId }) {
  const [d, setD] = useState(null);
  const [clarMsg, setClarMsg] = useState("");
  const [recipient, setRecipient] = useState("");

  const load = useCallback(() => {
    if (!logId) return;
    api.get(`/audit-logs/${logId}`).then((r) => {
      setD(r.data);
      const op = (r.data.user_email || "operatore").split("@")[0];
      const obj = r.data.object_label || r.data.action_label || "questo movimento";
      setClarMsg(`Ciao ${op}, ti chiedo cortesemente un chiarimento su questa modifica effettuata il ${fmtDT(r.data.at)} relativa a ${obj}. Puoi indicarmi il motivo dell'intervento?`);
      setRecipient(r.data.user_email || "");
    }).catch(() => setD(null));
  }, [logId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const logClar = async (channel) => {
    try { await api.post(`/audit-logs/${logId}/clarifications`, { channel, recipient, message: clarMsg }); load(); }
    catch { /* non-blocking */ }
  };
  const sendEmail = () => {
    if (!recipient) return toast.error("Destinatario mancante");
    window.open(`mailto:${recipient}?subject=${encodeURIComponent("Richiesta chiarimento movimento Urban OS")}&body=${encodeURIComponent(clarMsg)}`, "_blank");
    logClar("email"); toast.success("Email aperta · chiarimento registrato");
  };
  const sendWhatsApp = () => {
    const num = onlyDigits(recipient);
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(clarMsg)}`, "_blank");
    logClar("whatsapp"); toast.success("WhatsApp aperto · chiarimento registrato");
  };
  const getPdfBlob = async () => {
    const res = await api.get(`/audit-logs/${logId}/pdf`, { responseType: "blob" });
    return URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  };
  const downloadPdf = async () => {
    try { const url = await getPdfBlob(); const a = document.createElement("a"); a.href = url; a.download = `movimento_${logId.slice(0, 8)}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); }
    catch { toast.error("Errore PDF"); }
  };
  const printPdf = async () => {
    try { const url = await getPdfBlob(); const w = window.open(url, "_blank"); if (w) w.onload = () => w.print(); }
    catch { toast.error("Errore stampa"); }
  };
  const copyLink = () => {
    const link = `${window.location.origin}/audit?movimento=${logId}`;
    navigator.clipboard?.writeText(link).then(() => toast.success("Link interno copiato")).catch(() => toast.error("Impossibile copiare"));
  };

  const Row = ({ label, value, mono }) => (
    <div className="flex gap-3 border-b border-slate-100 py-2 text-sm">
      <span className="w-40 shrink-0 text-slate-500">{label}</span>
      <span className={`font-medium text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="audit-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock size={16} className="text-primary" /> Dettaglio movimento</DialogTitle>
          <DialogDescription className="flex items-center gap-1 text-xs"><ShieldCheck size={12} /> Evento immutabile · non modificabile né cancellabile</DialogDescription>
        </DialogHeader>

        {!d ? <div className="py-10 text-center text-sm text-slate-400">Caricamento…</div> : (
          <div className="space-y-5">
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <Button data-testid="audit-print" size="sm" variant="outline" className="gap-1.5" onClick={printPdf}><Printer size={14} /> Stampa</Button>
              <Button data-testid="audit-download-pdf" size="sm" variant="outline" className="gap-1.5" onClick={downloadPdf}><FileDown size={14} /> Scarica PDF</Button>
              <Button data-testid="audit-send-email" size="sm" variant="outline" className="gap-1.5" onClick={sendEmail}><Mail size={14} /> Email</Button>
              <Button data-testid="audit-send-whatsapp" size="sm" variant="outline" className="gap-1.5" onClick={sendWhatsApp}><MessageCircle size={14} /> WhatsApp</Button>
              <Button data-testid="audit-copy-link" size="sm" variant="outline" className="gap-1.5" onClick={copyLink}><Link2 size={14} /> Copia link interno</Button>
            </div>

            {/* Detail */}
            <div>
              <Row label="Data e ora" value={fmtDT(d.at)} />
              <Row label="Utente" value={d.user_email} />
              <Row label="Ruolo" value={d.user_role} />
              <Row label="Appartamento" value={d.apartment_name} />
              <Row label="Prenotazione" value={d.booking_ref} />
              <div className="flex gap-3 border-b border-slate-100 py-2 text-sm">
                <span className="w-40 shrink-0 text-slate-500">Tipo azione</span>
                <Badge className="bg-slate-100 text-slate-700">{d.action_label}</Badge>
              </div>
              <Row label="Oggetto" value={<>{d.object_label}{d.amount != null && <span className="ml-1 font-semibold text-rose-600">({eur(d.amount)})</span>}</>} />
              <Row label="Valore precedente" value={d.old_value} />
              <Row label="Valore nuovo" value={d.new_value} />
              <Row label="Motivazione" value={d.reason} />
              <Row label="Note interne" value={d.internal_notes} />
              <Row label="IP" value={d.ip} mono />
              <Row label="Dispositivo" value={d.device} />
            </div>

            {/* Clarification */}
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <h4 className="mb-2 text-sm font-semibold text-amber-900">Richiedi chiarimento</h4>
              <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Input data-testid="audit-clar-recipient" className="sm:col-span-1 bg-white" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Email o telefono" />
              </div>
              <Textarea data-testid="audit-clar-message" className="bg-white" rows={3} value={clarMsg} onChange={(e) => setClarMsg(e.target.value)} />
              <div className="mt-2 flex gap-2">
                <Button data-testid="audit-clar-email" size="sm" className="gap-1.5 bg-primary hover:bg-primary/90" onClick={sendEmail}><Mail size={14} /> Invia via Email</Button>
                <Button data-testid="audit-clar-whatsapp" size="sm" variant="outline" className="gap-1.5" onClick={sendWhatsApp}><MessageCircle size={14} /> Invia via WhatsApp</Button>
              </div>
            </div>

            {/* Clarification history */}
            <div>
              <h4 className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-slate-400"><Clock size={12} /> Cronologia chiarimenti ({d.clarifications?.length || 0})</h4>
              <div className="space-y-1.5" data-testid="audit-clar-history">
                {(d.clarifications || []).map((c) => (
                  <div key={c.id} className="rounded border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="flex items-center gap-1">{c.channel === "whatsapp" ? <MessageCircle size={12} className="text-emerald-600" /> : <Mail size={12} className="text-blue-600" />} {c.channel} → {c.recipient}</span>
                      <span>{fmtDT(c.sent_at)} · {c.sent_by}</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-600">{c.message}</div>
                  </div>
                ))}
                {(!d.clarifications || d.clarifications.length === 0) && <p className="text-xs text-slate-400">Nessun chiarimento richiesto</p>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
